// src/components/sidebar.js
import React from 'react';
import { NavLink } from 'react-router-dom';
import './sidebar.css';
import RoleSwitcher from '../components/RoleSwitcher';

const Sidebar = ({ currentUser, facultyProfile, handleSignOut, tenantConfig }) => {
  const isSuperAdmin = currentUser?.isSuperAdmin === true;
  
  // Use currentUser.role directly (updated by role switching)
  const userRole = (currentUser?.role || '').toLowerCase();
  const secondaryRoles = currentUser?.secondaryRoles || [];
  const hasMultipleRoles = secondaryRoles.length > 0;

  // Format role names for display
  const formatRoleName = (role) => {
    const roleMap = {
      'faculty/staff': 'Faculty/Staff',
      'hr': 'HR',
      'system administrator': 'System Administrator',
      'educational administrator': 'Educational Administrator',
      'volunteer manager': 'Volunteer Manager',
      'volunteer': 'Volunteer',
      'outside sub': 'Outside Substitute',
      'tenant setup admin': 'Tenant Setup Admin',
      'staff': 'Staff'
    };
    return roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Check for Tenant Setup Admin role
  const isTenantSetupAdmin = userRole === 'tenant setup admin';

  const isPrivilegedUser = isSuperAdmin || userRole === 'system administrator' || userRole === 'educational administrator';
  const isSystemAdmin = isSuperAdmin || userRole === 'system administrator';
  const isFacultyOrStaff = userRole === 'faculty/staff' || userRole === 'staff';
  const isOutsideSub = userRole === 'outside sub';
  const isHR = isSuperAdmin || userRole === 'hr';

  // UPDATED Volunteer role checks - check both primary role AND if they're registered as a volunteer
  const isPrimaryVolunteer = userRole === 'volunteer';
  const isRegisteredVolunteer = currentUser?.isAlsoVolunteer === true || currentUser?.hasVolunteerAccess === true || isPrimaryVolunteer;
  const isVolunteerManager = userRole === 'volunteer manager';
  const canManageVolunteers = isVolunteerManager || isPrivilegedUser;

  // For backward compatibility
  const isVolunteer = isPrimaryVolunteer;

  // UPDATED: Volunteer Managers should have employee privileges too
  const hasEmployeePrivileges = isFacultyOrStaff || isPrivilegedUser || isHR || isVolunteerManager;
  const hasSupervisoryRole = currentUser?.isSupervisor || isPrivilegedUser || isHR || isVolunteerManager;

  // Get enabled modules from tenant config (default to all enabled if not set)
  const enabledModules = tenantConfig?.enabledModules || {
    absenceManagement: true,
    substituteManagement: true,
    subPayManagement: true,
    volunteerManagement: false
  };

  console.log('Sidebar - tenantConfig:', tenantConfig);
  console.log('Sidebar - enabledModules:', enabledModules);
  console.log('Sidebar - userRole:', userRole);
  console.log('Sidebar - secondaryRoles:', secondaryRoles);
  console.log('Sidebar - volunteer status:', { 
    isPrimaryVolunteer, 
    isRegisteredVolunteer, 
    hasVolunteerAccess: currentUser?.hasVolunteerAccess,
    isAlsoVolunteer: currentUser?.isAlsoVolunteer 
  });

  let navLinks = [];

  if (currentUser) {
    // Special handling for Tenant Setup Admin - ONLY show Tenant Creation
    if (isTenantSetupAdmin) {
      navLinks.push({ path: '/tenant-setup', label: 'Tenant Creation' });
    } else {
      // Normal navigation for all other roles
      navLinks.push({ path: '/', label: 'Home' });

      if (isSuperAdmin) {
        navLinks.push({ path: '/super-admin', label: 'Tenant Creation' });
      }

      // Only show absence-related features if Absence Management is enabled
      if (enabledModules.absenceManagement) {
        if (hasEmployeePrivileges) {
          navLinks.push({ path: '/request-absence', label: 'Request Absence' });
        }
        if (hasSupervisoryRole) {
          navLinks.push({ path: '/create-absence-on-behalf', label: 'Create Absence For...' });
        }
      }

      // Only show substitute-related features if Substitute Management is enabled
      if (enabledModules.substituteManagement) {
        // Request a Sub - available to Faculty/Staff, privileged users, and HR (removed HR exclusion)
        if ((isFacultyOrStaff || isPrivilegedUser || isHR || isVolunteerManager) && !isVolunteerManager) {
          navLinks.push({ path: '/request-sub', label: 'Request a Sub' });
        }
        
        // Available Signups - available to Faculty/Staff, privileged users, HR, and Outside Subs (removed HR exclusion)
        if ((isFacultyOrStaff || isPrivilegedUser || isHR || isVolunteerManager) && !isVolunteerManager || isOutsideSub) {
          navLinks.push({ path: '/available-signups', label: 'Available Signups' });
        }
        
        // My Relief Roster - available to most users (removed HR exclusion)
        if (!isTenantSetupAdmin) {
          navLinks.push({ path: '/my-roster', label: 'My Relief Roster' });
        }

        // UPDATED: Substitute Dashboard - for administrative substitute management
        if (isPrivilegedUser || isHR) {
          navLinks.push({ path: '/substitute-manager-dashboard', label: 'Substitute Dashboard' });
        }
      }

      // Sub Pay Management Navigation - NEW SECTION
      if (enabledModules.subPayManagement && (isHR || isPrivilegedUser)) {
        navLinks.push({ path: '/sub-pay', label: 'Sub Pay Management' });
      }

      // Volunteer Management Navigation
      if (enabledModules.volunteerManagement) {
        // Manager-specific links
        if (isVolunteerManager) {
          // Volunteer Managers get ALL volunteer links on the sidebar
          navLinks.push({ path: '/volunteer-dashboard', label: 'Volunteer Dashboard' });
          navLinks.push({ path: '/volunteer-events', label: 'Volunteer Events' });
          //navLinks.push({ path: '/event-manager', label: 'Event Manager' });
          navLinks.push({ path: '/volunteer-directory', label: 'Volunteer Directory' });
          navLinks.push({ path: '/volunteer-approvals', label: 'Registration Approvals' });
          navLinks.push({ path: '/available-volunteer-events', label: 'Available Events' });
        } else if (canManageVolunteers) {
          // Other privileged users ONLY get the dashboard (other links are nested inside)
          navLinks.push({ path: '/volunteer-dashboard', label: 'Volunteer Dashboard' });
        }
        
        // UPDATED: Show volunteer links to anyone registered as a volunteer (even if it's not their primary role)
        if (isRegisteredVolunteer) {
          // Add a divider or section header if they have other roles
          if (!isPrimaryVolunteer && navLinks.length > 0) {
            // This creates a visual separation for volunteer-specific items
            navLinks.push({ 
              path: null, 
              label: '— Volunteer —', 
              isDivider: true 
            });
          }
          
          navLinks.push({ path: '/my-volunteer-schedule', label: 'My Schedule' });
          navLinks.push({ path: '/available-volunteer-events', label: 'Available Events' });
          navLinks.push({ path: '/volunteer-settings', label: 'Volunteer Settings' });
        }
      }

      // These features are always available regardless of modules
      if (hasSupervisoryRole) {
        navLinks.push({ path: '/supervisor-dashboard', label: 'Supervisor Dashboard' });
      }

      // Admin Dashboard - HR and Educational Administrator only
      if (isPrivilegedUser || isHR) {
        navLinks.push({ path: '/admin-dashboard', label: 'Admin Dashboard' });
      }
      
      if (isPrivilegedUser) {
        navLinks.push({ path: '/analytics', label: 'Analytics' });
        navLinks.push({ path: '/reports', label: 'Reports' });
      }
      
      if (isSystemAdmin) {
        navLinks.push({ path: '/admin', label: 'Admin' });
        navLinks.push({ path: '/admin/system-setup', label: 'System Setup' });
      }

      // Volunteer Setup access for both System Admins and Volunteer Managers
      if (isSystemAdmin || isVolunteerManager) {
        navLinks.push({ path: '/admin/volunteer-setup', label: 'Volunteer Setup' });
      }

      // NEW: User Settings - Available to all users (except Tenant Setup Admin)
      navLinks.push({ path: '/user-settings', label: 'User Settings' });
    }
  }

  console.log('Sidebar - Final navLinks:', navLinks);

  const onSignOutClick = async () => {
    if (handleSignOut) {
      await handleSignOut();
    } else {
      console.error("Sign out handler not provided to Sidebar component");
    }
  };

  return (
    <div className="sidebar">
      <div className="logo-container">
        <img
          src={`${process.env.PUBLIC_URL}/Relief_Roster_logo_med.png`}
          alt="Relief Roster Logo"
          className="logo"
        />
      </div>
      {currentUser && (
        <>
          <div className="user-info" style={{
            padding: '10px 20px',
            textAlign: 'center',
            borderBottom: '1px solid #444'
          }}>
            <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
              {facultyProfile?.displayName || facultyProfile?.name || currentUser.displayName || currentUser.email}
            </p>

            {/* ROLE SWITCHER - Clean production version */}
            <RoleSwitcher currentUser={currentUser} />

            {/* Show dual role indicator if user is staff AND volunteer */}
            {isRegisteredVolunteer && !isPrimaryVolunteer && (
              <p style={{ 
                margin: '5px 0', 
                fontSize: '0.75em', 
                color: '#28a745',
                fontWeight: 'bold'
              }}>
                + Volunteer
              </p>
            )}

            {/* Supervisor indicator only */}
            {currentUser.isSupervisor && !isTenantSetupAdmin && (
              <p style={{ margin: '5px 0', fontSize: '0.75em', color: 'cyan' }}>
                Supervisor
              </p>
            )}
            
            {/* Show available roles indicator for multi-role users */}
            {hasMultipleRoles && (
              <p style={{ margin: '5px 0 0 0', fontSize: '0.7em', color: '#888' }}>
                Available: {[userRole, ...secondaryRoles].map(formatRoleName).join(', ')}
              </p>
            )}
          </div>

          <nav className="nav-links">
            {navLinks.map((link, index) => {
              // Handle divider items
              if (link.isDivider) {
                return (
                  <div 
                    key={`divider-${index}`} 
                    style={{
                      padding: '10px 20px',
                      color: '#888',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      borderTop: '1px solid #444',
                      marginTop: '10px',
                      paddingTop: '15px'
                    }}
                  >
                    {link.label}
                  </div>
                );
              }
              
              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                >
                  {link.label}
                </NavLink>
              );
            })}
          </nav>
          
          {/* Show helpful info for Tenant Setup Admins */}
          {isTenantSetupAdmin && (
            <div style={{
              padding: '15px',
              margin: '10px',
              backgroundColor: '#2a3f5f',
              borderRadius: '5px',
              fontSize: '0.8em',
              color: '#bbb'
            }}>
              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#ffa500' }}>
                Tenant Setup Mode
              </p>
              <p style={{ margin: 0 }}>
                You have access to the manual tenant setup wizard only.
              </p>
            </div>
          )}

          {/* Show helpful info for Volunteers */}
          {isRegisteredVolunteer && (
            <div style={{
              padding: '15px',
              margin: '10px',
              backgroundColor: '#28a745',
              borderRadius: '5px',
              fontSize: '0.8em',
              color: '#fff'
            }}>
              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                {isPrimaryVolunteer ? 'Volunteer' : 'Volunteer Access'}
              </p>
              <p style={{ margin: 0 }}>
                {isPrimaryVolunteer 
                  ? 'View your schedule and sign up for available volunteer opportunities.'
                  : 'You have additional access to volunteer opportunities and scheduling.'
                }
              </p>
            </div>
          )}

          {/* Module status info for System Administrators */}
          {isSystemAdmin && (
            <div style={{
              padding: '10px',
              margin: '10px',
              backgroundColor: '#1a1a1a',
              borderRadius: '3px',
              fontSize: '0.7em',
              color: '#888',
              border: '1px solid #333'
            }}>
              <div style={{ color: '#ffa500', fontWeight: 'bold', marginBottom: '5px' }}>Module Status:</div>
              <div>Absence: {enabledModules.absenceManagement ? '✓' : '✗'}</div>
              <div>Substitute: {enabledModules.substituteManagement ? '✓' : '✗'}</div>
              <div>Sub Pay: {enabledModules.subPayManagement ? '✓' : '✗'}</div>
              <div style={{ color: enabledModules.volunteerManagement ? '#28a745' : '#dc3545' }}>
                Volunteer: {enabledModules.volunteerManagement ? '✓' : '✗'}
              </div>
            </div>
          )}
          
          <div className="sidebar-footer">
            <button onClick={onSignOutClick} className="logout-button">
              Sign Out
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;